import { Subject } from 'rxjs'
const ResizeObserver = (window as any).ResizeObserver
const LINK_REMOVE_DELAY = 800

const supportedMime = [
  'application/pdf'
]

export function isFilePreviewSupported (mimeType: string): boolean {
  return supportedMime.includes(mimeType)
}

export const filePreviewOpenRequest = new Subject<{
  fileUrl: string,
  name: string,
  origin: 'table' | 'report'
}>()

export const previewDownloadRequest = new Subject<{
  fileUrl: string,
  name: string,

}>()

export function triggerDownload (url: string, filename: string) {
  const anchorElement = document.createElement('a')
  anchorElement.href = url
  anchorElement.style.visibility = 'hidden' // Hide the element
  anchorElement.style.position = 'fixed' // Hide the element
  anchorElement.style.left = '-200px' // Move the element off-screen
  anchorElement.style.top = '-200px' // Move the element off-screen
  anchorElement.download = filename
  document.body.appendChild(anchorElement) // Append to body to ensure it works in all browsers
  anchorElement.click()
  setTimeout(() => {
    anchorElement.remove()
  }, LINK_REMOVE_DELAY)
}

export const observeResizeEvent = (element: HTMLElement, eventName: string, debounceDelay: number): any => {
  const _brodcast = (element: HTMLElement, eventName: string): void => {
    const event = new CustomEvent(eventName, {
      detail: {
        width: element.offsetWidth
      }
    })
    element.dispatchEvent(event)
  }
  const trigger = debounce(_brodcast, debounceDelay)
  const observer = new ResizeObserver((entries: any[]) => {
    for (const entry of entries) {
      if (element === entry.target) {
        trigger(element, eventName)
      }
    }
  })

  observer.observe(element)
  return observer
}

export class ZoomAdapter {
  private element: HTMLElement;
  private isPinching = false;
  private initialDistance = 0;
  private eventName = 'CUSTOM_ZOOM'

  constructor (element: HTMLElement, eventName: string) {
    this.element = element
    this.eventName = eventName
    this.element.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false })
    this.element.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false })
    this.element.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: false })
    this.element.addEventListener('wheel', this.onWheel.bind(this), { passive: false })
  }

  private onTouchStart (event: TouchEvent | any): void {
    if (event.touches.length === 2) {
      event.preventDefault()
      this.isPinching = true
      const touch1 = event.touches[0]
      const touch2 = event.touches[1]
      this.initialDistance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY)
    }
  }

  private onTouchMove (event: TouchEvent | any): void {
    if (this.isPinching && event.touches.length === 2) {
      event.preventDefault()
      const touch1 = event.touches[0]
      const touch2 = event.touches[1]
      const currentDistance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY)
      const zoomFactor = currentDistance / this.initialDistance
      this.element.dispatchEvent(new CustomEvent(this.eventName, { detail: { zoomFactor } }))
    }
  }

  private onWheel (event: WheelEvent | any): void {
    if (event.ctrlKey) {
      event.preventDefault()
      const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1
      this.element.dispatchEvent(new CustomEvent(this.eventName, { detail: { zoomFactor } }))
    }
  }

  private onTouchEnd (): void {
    this.isPinching = false
    this.initialDistance = 0
  }

  public unsubscribe (): void {
    this.element.removeEventListener('touchstart', this.onTouchStart, { passive: false } as any)
    this.element.removeEventListener('touchmove', this.onTouchMove, { passive: false } as any)
    this.element.removeEventListener('touchend', this.onTouchEnd, { passive: false } as any)
    this.element.removeEventListener('wheel', this.onWheel, { passive: false } as any)
  }
}

export function debounce<T extends (...args: any[]) => unknown>(func: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let lastArgs: Parameters<T> | null = null
  return function (this: unknown, ...args: Parameters<T>) {
    lastArgs = args
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      timeoutId = null
      const argsToUse = lastArgs
      lastArgs = null
      if (argsToUse) {
        // Arrow callback captures the caller's `this` from the wrapper function
        (func as any).apply(this, argsToUse)
      }
    }, delay)
  }
}
